import { z } from "zod";
import { isoDateSchema, isoDateTimeSchema, uuidSchema } from "./common.ts";

/**
 * AI Assistant (strategy repo `teamna/strategy/ai-assistant-v1.md`): the owner or an admin asks a
 * question, and Teamna answers from live data with tools. Routes under
 * `/owner/businesses/:businessId/assistant/...`.
 *
 * Every number on the screen comes from a tool result, never from text that the model writes. So an
 * answer has two parts: `text` (the sentences of the model) and `blocks` (tables that the server
 * built from the tool results). The screen draws the blocks as they are.
 */

export const assistantInputModes = ["text", "voice"] as const;
export const assistantInputModeSchema = z.enum(assistantInputModes);
export type AssistantInputMode = z.infer<typeof assistantInputModeSchema>;

/**
 * How a turn ended.
 * - `answered`: a final answer.
 * - `asked_back`: the model asked a question back (for example, which of two sessions). Set by the
 *   model through the answer, so V1 counts it as `answered` unless the model said so.
 * - `refused`: the request is outside what the AI Assistant does.
 * - `limit`: the turn used all its tool steps, or the business used its turns for the day.
 * - `error`: the model or a tool failed. The text says so in plain words.
 * - `no_tool`: no tool gives the answer. The model called `no_tool_fits` with what is missing, and
 *   answered the nearest question it could. Control Center lists these: they show the next tool to build.
 */
export const assistantOutcomes = ["answered", "asked_back", "refused", "limit", "error", "no_tool"] as const;
export const assistantOutcomeSchema = z.enum(assistantOutcomes);
export type AssistantOutcome = z.infer<typeof assistantOutcomeSchema>;

/** How the screen formats a cell. `money` is fils, `percent` is 0 to 100, `date` is `YYYY-MM-DD`. */
export const assistantColumnKinds = ["text", "number", "percent", "money", "date", "datetime"] as const;
export const assistantColumnSchema = z.object({
  key: z.string(),
  label: z.string(),
  kind: z.enum(assistantColumnKinds),
});
export type AssistantColumn = z.infer<typeof assistantColumnSchema>;

const assistantCellSchema = z.union([z.string(), z.number(), z.null()]);

/** A table built from one tool result. `total` is the last row, drawn in bold, when it is set. */
export const assistantTableBlockSchema = z.object({
  type: z.literal("table"),
  title: z.string(),
  /** One short line under the title: the period, the filter. */
  caption: z.string().nullable(),
  columns: z.array(assistantColumnSchema),
  rows: z.array(z.record(z.string(), assistantCellSchema)),
  total: z.record(z.string(), assistantCellSchema).nullable(),
  /** The tool result had more rows than the table shows. */
  truncated: z.boolean(),
  /**
   * The answer in one line, written by the tool, never by the model: "34 came today so far". The
   * screen shows it big, above the table. Optional: older turns have none.
   */
  headline: z.string().nullable().optional(),
  /**
   * One more level of detail on tap ("See each swimmer"): the same read tool with this input. The tap
   * runs the tool with no model call (`POST .../turns/:turnId/more`). Optional: older turns have none.
   */
  more: z.object({ label: z.string(), tool: z.string(), input: z.record(z.string(), z.unknown()) }).nullable().optional(),
});
export type AssistantTableBlock = z.infer<typeof assistantTableBlockSchema>;

/** What AI Assistant found, as short lines: the start of a suggestion chat. */
export const assistantFactsBlockSchema = z.object({
  type: z.literal("facts"),
  title: z.string(),
  lines: z.array(z.string()),
});
export type AssistantFactsBlock = z.infer<typeof assistantFactsBlockSchema>;

/**
 * A proposal: a change that waits for the owner. It points at its `assistant_actions` row; the
 * status (on the table, replaced, confirmed) comes from `actions` in the conversation, because it
 * changes after the turn. Options of one choice share `optionGroup`, numbered by `optionIndex`.
 */
export const assistantProposalBlockSchema = z.object({
  type: z.literal("proposal"),
  actionId: uuidSchema,
  version: z.number().int().min(1),
  title: z.string(),
  lines: z.array(z.string()),
  /** The message text that people get, when the action sends one. */
  draft: z.string().nullable(),
  /** The label of the Confirm button: "Resend 2 links". */
  cta: z.string(),
  /** What this version changed from the one before, in one line. */
  changed: z.string().nullable(),
  optionGroup: z.string().nullable(),
  optionIndex: z.number().int().min(0).nullable(),
});
export type AssistantProposalBlock = z.infer<typeof assistantProposalBlockSchema>;

/** The end of an action: what Teamna did, or why it could not. */
export const assistantResultBlockSchema = z.object({
  type: z.literal("result"),
  ok: z.boolean(),
  text: z.string(),
});
export type AssistantResultBlock = z.infer<typeof assistantResultBlockSchema>;

/**
 * A message for the owner to share outside Teamna (Studio A1): the open spots message of Home H2
 * (`openSpotsMessage`), with each time and its Book link. The screen gives the same button as the
 * Home card: Copy on Desk, the share sheet on the phone. Nothing is sent by Teamna, so it is not a
 * plan and has no Confirm.
 */
export const assistantShareBlockSchema = z.object({
  type: z.literal("share"),
  title: z.string(),
  /** The message, exactly as the owner pastes it. */
  text: z.string(),
  /** For the `open_spots_shared` event: the open places and the sessions in the message. */
  spots: z.number().int().min(0),
  sessions: z.number().int().min(0),
});
export type AssistantShareBlock = z.infer<typeof assistantShareBlockSchema>;

/**
 * How a chart draws its table (decisions A4, A5 and A7 of the generic data plan, 2026-10-29): bars
 * (across for names, up for time), a line for a long time trend, stacked bars for a time or a name
 * split by a second group, and KPI tiles for totals with no group.
 */
export const assistantChartKinds = ["bar", "line", "stacked_bar", "kpi"] as const;
export const assistantChartKindSchema = z.enum(assistantChartKinds);
export type AssistantChartKind = z.infer<typeof assistantChartKindSchema>;

/**
 * A chart of the table block that comes right after it. It has no numbers of its own: it names the
 * columns of that table to draw, so the chart and the table can never differ. The screen shows the
 * two as one card with a Chart and Table switch (`assistantChartModel`).
 *
 * Only a client that sends `X-Teamna-Blocks: chart` gets this block. Older apps get the table only.
 */
export const assistantChartBlockSchema = z.object({
  type: z.literal("chart"),
  kind: assistantChartKindSchema,
  /** The column of the names or the time on the axis. Null for KPI tiles. */
  x: z.string().nullable(),
  /** Stacked bars: the column whose values are the parts of each bar. */
  stack: z.string().nullable(),
  /** The metric columns to draw, the first one is the main one. */
  series: z.array(z.string()).min(1).max(4),
  /** The x column is time, in order: the last value counts only up to now when `partialLast`. */
  time: z.boolean(),
  partialLast: z.boolean(),
});
export type AssistantChartBlock = z.infer<typeof assistantChartBlockSchema>;

/** The block types that a client must say it can draw (`X-Teamna-Blocks`). The others every client draws. */
export const assistantOptionalBlockTypes = ["chart"] as const;
export type AssistantOptionalBlockType = (typeof assistantOptionalBlockTypes)[number];

export const assistantBlockSchema = z.discriminatedUnion("type", [
  assistantTableBlockSchema,
  assistantChartBlockSchema,
  assistantFactsBlockSchema,
  assistantProposalBlockSchema,
  assistantResultBlockSchema,
  assistantShareBlockSchema,
]);
export type AssistantBlock = z.infer<typeof assistantBlockSchema>;

/** One tool call of a turn, for the "N tool calls" list. Never the full result. */
export const assistantToolCallSchema = z.object({
  name: z.string(),
  input: z.record(z.string(), z.unknown()),
  /** One line: "12 rows", "No rows", or the error. */
  summary: z.string(),
  ok: z.boolean(),
});
export type AssistantToolCall = z.infer<typeof assistantToolCallSchema>;

/** The tool the model calls when no other tool gives the answer (`assistant/tools.ts`). */
export const ASSISTANT_GAP_TOOL = "no_tool_fits";

/** The shape of a question that no tool answers. Analytics gets this enum, never the question. */
export const assistantGapKinds = ["count", "list", "compare", "trend", "action", "other"] as const;
export const assistantGapKindSchema = z.enum(assistantGapKinds);
export type AssistantGapKind = z.infer<typeof assistantGapKindSchema>;

/** What the answer could not give: the model's short words for it, and its kind. Null when a tool fit. */
export function assistantGapOf(calls: ReadonlyArray<Pick<AssistantToolCall, "name" | "input" | "ok">>): { need: string; kind: AssistantGapKind } | null {
  const call = calls.find((item) => item.name === ASSISTANT_GAP_TOOL && item.ok);
  if (!call) return null;
  const kind = assistantGapKindSchema.safeParse(call.input.kind);
  return { need: typeof call.input.need === "string" ? call.input.need : "", kind: kind.success ? kind.data : "other" };
}

/**
 * `event` is a line between turns that nobody wrote: "Checked again when you opened this", "Not now",
 * "Marked done by you". The screen draws it small and centred.
 */
export const assistantTurnRoles = ["user", "assistant", "event"] as const;

/** Thumbs up or down on one answer, by the person who asked. */
export const assistantRatings = ["up", "down"] as const;
export const assistantRatingSchema = z.enum(assistantRatings);
export type AssistantRating = z.infer<typeof assistantRatingSchema>;

/** Why an answer was not good. An enum, so analytics never gets text that a person typed. */
export const assistantRatingReasons = [
  "wrong_answer",
  "wrong_numbers",
  "did_not_understand",
  "could_not_do_it",
  "wrong_language",
  "too_long",
  "other",
] as const;
export const assistantRatingReasonSchema = z.enum(assistantRatingReasons);
export type AssistantRatingReason = z.infer<typeof assistantRatingReasonSchema>;

export const ASSISTANT_RATING_REASON_LABELS: Record<AssistantRatingReason, string> = {
  wrong_answer: "The answer is wrong",
  wrong_numbers: "A number is wrong",
  did_not_understand: "It did not understand me",
  could_not_do_it: "It could not do what I asked",
  wrong_language: "Wrong language",
  too_long: "Too long",
  other: "Something else",
};

/**
 * What a prompt is about, from a small model that reads only the prompt (`assistant/topic.ts`).
 * Analytics gets the topic, never the prompt. `how_to` is a question about how to use Teamna.
 */
export const assistantTopics = [
  "payments",
  "sessions",
  "attendance",
  "members",
  "coaches",
  "messages",
  "packages",
  "numbers",
  "how_to",
  "out_of_scope",
  "other",
] as const;
export const assistantTopicSchema = z.enum(assistantTopics);
export type AssistantTopic = z.infer<typeof assistantTopicSchema>;

export const assistantTurnSchema = z.object({
  id: uuidSchema,
  role: z.enum(assistantTurnRoles),
  text: z.string(),
  inputMode: assistantInputModeSchema.nullable(),
  blocks: z.array(assistantBlockSchema),
  toolCalls: z.array(assistantToolCallSchema),
  outcome: assistantOutcomeSchema.nullable(),
  /** The thumbs of the person on an answer. Null: not rated, and always null on other turns. Optional for older clients. */
  rating: assistantRatingSchema.nullable().optional(),
  /** An answer of the model, which the person can rate. False for prompts, lines, openers and Confirm results. Optional for older clients. */
  rateable: z.boolean().optional(),
  createdAt: isoDateTimeSchema,
});
export type AssistantTurn = z.infer<typeof assistantTurnSchema>;

// ---------------------------------------------------------------------------
// Suggestions (cards) and their chats
// ---------------------------------------------------------------------------

/**
 * The kinds of suggestion that AI Assistant finds each morning at 07:00 (detectors in
 * `packages/core/src/assistant/detectors.ts`).
 * - `unpaid_links`: payment requests still open 7 days or more after they were sent.
 * - `session_no_coach`: a session tomorrow with people booked and no coach.
 * - `missed_in_row`: a participant who missed the last 3 sessions.
 *
 * Only where clients book for themselves (Studio A1, `booking_for = self`):
 * - `class_under_half`: the classes of tomorrow with fewer than half of the places booked.
 * - `pack_ending_unbooked`: packs that end in the next 7 days, with sessions left and nothing booked.
 * - `intro_no_next_pack`: clients whose newest pack is an intro offer bought 7 days ago or more.
 */
export const assistantCardKinds = [
  "unpaid_links",
  "session_no_coach",
  "missed_in_row",
  "class_under_half",
  "pack_ending_unbooked",
  "intro_no_next_pack",
] as const;
export const assistantCardKindSchema = z.enum(assistantCardKinds);
export type AssistantCardKind = z.infer<typeof assistantCardKindSchema>;

/** A suggestion: `open` waits, `snoozed` comes back at 07:00 the next day, the other two are finished. */
export const assistantCardStatuses = ["open", "snoozed", "done", "closed"] as const;
export type AssistantCardStatus = (typeof assistantCardStatuses)[number];

/**
 * The status of a chat as the list shows it, with its colour.
 * - `waiting`: a plan is on the table and waits for Confirm (amber).
 * - `new`: AI Assistant found it and this person did not open it yet (violet).
 * - `open`: a chat in progress (blue).
 * - `done`: confirmed or marked done (green).
 * - `snoozed`: "Not now", back at 07:00 tomorrow (grey, dashed).
 * - `closed`: closed with a reason (grey outline).
 */
export const assistantChatStatuses = ["waiting", "new", "open", "done", "snoozed", "closed"] as const;
export const assistantChatStatusSchema = z.enum(assistantChatStatuses);
export type AssistantChatStatus = z.infer<typeof assistantChatStatusSchema>;

/** Why the owner closed a suggestion. An enum, so analytics never gets text that a person typed. */
export const assistantCloseReasons = [
  "paid_cash",
  "coach_comes",
  "family_told_us",
  "handled_elsewhere",
  "not_a_problem",
  "wrong_signal",
] as const;
export const assistantCloseReasonSchema = z.enum(assistantCloseReasons);
export type AssistantCloseReason = z.infer<typeof assistantCloseReasonSchema>;

export const ASSISTANT_CLOSE_REASON_LABELS: Record<AssistantCloseReason, string> = {
  paid_cash: "They paid in cash",
  coach_comes: "The coach comes after all",
  family_told_us: "The family told us why",
  handled_elsewhere: "We handle it another way",
  not_a_problem: "Not a problem",
  wrong_signal: "Wrong signal",
};

/** The reasons the close bar offers for each kind, the most likely first. */
export const ASSISTANT_CLOSE_REASONS_BY_KIND: Record<AssistantCardKind, AssistantCloseReason[]> = {
  unpaid_links: ["paid_cash", "handled_elsewhere", "wrong_signal"],
  session_no_coach: ["coach_comes", "handled_elsewhere", "wrong_signal"],
  missed_in_row: ["family_told_us", "handled_elsewhere", "not_a_problem"],
  class_under_half: ["handled_elsewhere", "not_a_problem", "wrong_signal"],
  pack_ending_unbooked: ["handled_elsewhere", "not_a_problem", "wrong_signal"],
  intro_no_next_pack: ["handled_elsewhere", "not_a_problem", "wrong_signal"],
};

/** The changes AI Assistant can propose. Each one waits for Confirm; the model never commits. */
export const assistantActionTools = [
  "session_set_coach",
  "session_move",
  "session_cancel",
  "payment_reminder_send",
  "family_message_send",
  /** The renewal message of the Renewals list (Studio Home H3), to clients who book for themselves. */
  "renewal_send",
] as const;
export const assistantActionToolSchema = z.enum(assistantActionTools);
export type AssistantActionTool = z.infer<typeof assistantActionToolSchema>;

/**
 * - `proposed`: on the table (or one of the options).
 * - `replaced`: a newer version or another option took its place.
 * - `confirmed`: the owner confirmed, and Teamna did it.
 * - `stopped`: the chat ended (Not now, Close, Mark done) before a confirm.
 * - `stale`: the data changed after the dry run, so Confirm refused it.
 * - `failed`: the commit failed.
 */
export const assistantActionStatuses = ["proposed", "replaced", "confirmed", "stopped", "stale", "failed"] as const;
export type AssistantActionStatus = (typeof assistantActionStatuses)[number];

export const assistantActionSchema = z.object({
  id: uuidSchema,
  tool: assistantActionToolSchema,
  status: z.enum(assistantActionStatuses),
  version: z.number().int(),
  optionGroup: z.string().nullable(),
  optionIndex: z.number().int().nullable(),
  /** The newer action that replaced this one. */
  replacedById: uuidSchema.nullable(),
});
export type AssistantAction = z.infer<typeof assistantActionSchema>;

/** One suggestion as Home shows it. `conversationId` is the chat of the signed-in person, if any. */
export const assistantCardSchema = z.object({
  id: uuidSchema,
  kind: assistantCardKindSchema,
  title: z.string(),
  /** One line under the title, from the facts at the last check. */
  subtitle: z.string(),
  /** A short tag with a deadline: "Tomorrow". */
  tag: z.string().nullable(),
  chatStatus: assistantChatStatusSchema,
  conversationId: uuidSchema.nullable(),
  createdAt: isoDateTimeSchema,
});
export type AssistantCard = z.infer<typeof assistantCardSchema>;

export const assistantCardListSchema = z.object({ cards: z.array(assistantCardSchema) });
export type AssistantCardList = z.infer<typeof assistantCardListSchema>;

export const assistantCardParamSchema = z.object({ businessId: uuidSchema, cardId: uuidSchema });
export const assistantActionParamSchema = z.object({ businessId: uuidSchema, actionId: uuidSchema });

/** Where the owner opened a suggestion from, for analytics. */
export const assistantEntries = ["home_sheet", "assistant_page"] as const;
export const assistantOpenCardRequestSchema = z.object({ entry: z.enum(assistantEntries).default("home_sheet") });

export const assistantConversationSummarySchema = z.object({
  id: uuidSchema,
  title: z.string(),
  status: assistantChatStatusSchema,
  /** A suggestion chat names its kind; a question the person asked is null. */
  cardKind: assistantCardKindSchema.nullable(),
  /** The last line of the chat, for the list. */
  preview: z.string(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type AssistantConversationSummary = z.infer<typeof assistantConversationSummarySchema>;

export const assistantConversationListQuerySchema = z.object({
  /** Words in the title or in a prompt. */
  q: z.string().trim().max(100).optional(),
});

/**
 * The chat list of the AI Assistant page: the person's conversations, and the open suggestions that
 * this person did not open yet (status `new`, `conversationId` null).
 */
export const assistantChatListItemSchema = z.object({
  conversationId: uuidSchema.nullable(),
  cardId: uuidSchema.nullable(),
  cardKind: assistantCardKindSchema.nullable(),
  title: z.string(),
  status: assistantChatStatusSchema,
  preview: z.string(),
  updatedAt: isoDateTimeSchema,
});
export type AssistantChatListItem = z.infer<typeof assistantChatListItemSchema>;

export const assistantConversationListSchema = z.object({
  conversations: z.array(assistantChatListItemSchema),
});
export type AssistantConversationList = z.infer<typeof assistantConversationListSchema>;

export const assistantConversationSchema = assistantConversationSummarySchema.extend({
  turns: z.array(assistantTurnSchema),
  cardId: uuidSchema.nullable(),
  /** Every proposal of the chat with its current status. */
  actions: z.array(assistantActionSchema),
  /** Questions an owner may have next, as text. A tap sends it as a prompt. */
  chips: z.array(z.string()),
  /** The reasons the close bar offers. Empty for a question chat (it has no Close). */
  closeReasons: z.array(z.object({ key: assistantCloseReasonSchema, label: z.string() })),
});
export type AssistantConversation = z.infer<typeof assistantConversationSchema>;

export const assistantConversationParamSchema = z.object({ businessId: uuidSchema, conversationId: uuidSchema });

/** The longest prompt. A voice note of 60 seconds is about 900 characters. */
export const ASSISTANT_PROMPT_MAX = 2000;

/**
 * How long Desk and the app wait for an answer. One answer is several model calls with tools, so it
 * can take far longer than a normal request. On a local API with the CLI client each step starts one
 * `claude -p`, and the API waits up to 255 seconds (`apps/desk-api/src/index.ts`).
 */
export const ASSISTANT_TURN_TIMEOUT_MS = 255_000;

export const assistantTurnRequestSchema = z.object({
  /** Missing: start a new conversation. */
  conversationId: uuidSchema.optional(),
  text: z.string().trim().min(1).max(ASSISTANT_PROMPT_MAX),
  inputMode: assistantInputModeSchema.default("text"),
});
export type AssistantTurnRequest = z.infer<typeof assistantTurnRequestSchema>;

/**
 * Voice (spec item 7, built 2026-10-23): the browser records the question, the API sends the audio
 * to OpenAI `gpt-4o-transcribe` and gives back the text. The text goes into the compose box; the
 * person reads it and taps Send. Teamna keeps no audio.
 */
export const ASSISTANT_VOICE_SECONDS_MAX = 60;
/** The recorder formats of the browsers: Chrome and Firefox record WebM or Ogg (Opus), Safari MP4 (AAC). */
export const assistantVoiceMimeTypes = ["audio/webm", "audio/ogg", "audio/mp4", "audio/mpeg", "audio/wav"] as const;
export type AssistantVoiceMimeType = (typeof assistantVoiceMimeTypes)[number];
/** 60 seconds of AAC at 128 kbit/s is about 1 MB, so 1.4 MB as base64. */
export const ASSISTANT_VOICE_BASE64_MAX = 2_400_000;

/** Canonical base64 with its padding: `Buffer.from` would skip bad characters and decode the rest (review F1.1). */
export const BASE64_PATTERN = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

export const assistantTranscribeRequestSchema = z.object({
  /** The recording, base64, with no `data:` prefix. */
  audio: z.string().min(4).max(ASSISTANT_VOICE_BASE64_MAX).regex(BASE64_PATTERN, "Send the recording as base64."),
  mimeType: z.enum(assistantVoiceMimeTypes),
  /** The length the browser measured, for analytics and the limit. */
  seconds: z.number().min(0).max(ASSISTANT_VOICE_SECONDS_MAX + 5),
});
export type AssistantTranscribeRequest = z.infer<typeof assistantTranscribeRequestSchema>;

export const assistantTranscribeResponseSchema = z.object({
  /** Empty when the recording had no words. */
  text: z.string(),
});
export type AssistantTranscribeResponse = z.infer<typeof assistantTranscribeResponseSchema>;

export const assistantTurnResponseSchema = z.object({
  conversation: assistantConversationSchema,
  userTurn: assistantTurnSchema,
  assistantTurn: assistantTurnSchema,
});

/** Close a suggestion chat with a reason. */
export const assistantCloseRequestSchema = z.object({ reason: assistantCloseReasonSchema });
export type AssistantTurnResponse = z.infer<typeof assistantTurnResponseSchema>;

/** Thumbs up or down on an answer. `null` takes the rating back. A reason and a note only go with `down`. */
export const assistantRatingRequestSchema = z.object({
  rating: assistantRatingSchema.nullable(),
  reason: assistantRatingReasonSchema.nullable().default(null),
  /** Free text for Teamna, kept in the database only (never analytics). */
  note: z.string().trim().max(500).nullable().default(null),
});
export type AssistantRatingRequest = z.infer<typeof assistantRatingRequestSchema>;
export const assistantTurnParamSchema = z.object({ businessId: uuidSchema, turnId: uuidSchema });

/** `POST .../turns/:turnId/more`: the table of that answer whose More the person tapped. */
export const assistantMoreRequestSchema = z.object({ block: z.number().int().min(0).max(20) });
export type AssistantMoreRequest = z.infer<typeof assistantMoreRequestSchema>;

// ---------------------------------------------------------------------------
// The package (decided 2026-10-21)
// ---------------------------------------------------------------------------

/**
 * AI Assistant is a package. It is on when the business bought it (a second line on its Teamna
 * subscription) or Teamna staff gave it in Control Center (a grant, with an optional last day).
 * Owner and admin rows of `GET /v1/me/businesses` carry this; Desk and the app show the locked
 * screen from it.
 */
export const assistantPackageSchema = z.object({
  on: z.boolean(),
  /** The business pays for the package now. */
  purchased: z.boolean(),
  /** After Remove: the package stays on until this moment (the end of the paid period). */
  endsAt: isoDateTimeSchema.nullable(),
  /** Teamna gave it free. `until` is the last day, in the business's time zone; null has no end. */
  grant: z.object({ until: isoDateSchema.nullable() }).nullable(),
  /**
   * The owner or an admin can add the package in Desk now. False when billing is off on this
   * server, for a demo business, before the subscription exists, and while Desk is read-only.
   */
  canAdd: z.boolean(),
  priceAed: z.number().int(),
  /** The day of the first charge for an added package: the trial end, else the next renewal. */
  nextChargeAt: isoDateTimeSchema.nullable(),
  /** The business has a card on file. Without one, Desk asks for a card before the first charge. */
  hasCard: z.boolean(),
});
export type AssistantPackage = z.infer<typeof assistantPackageSchema>;

// ---------------------------------------------------------------------------
// Control Center
// ---------------------------------------------------------------------------

/** Control Center: the AI Assistant of one business, bought or given. */
export const adminAiAssistantSchema = z.object({
  on: z.boolean(),
  purchased: z.boolean(),
  /** When the business added the package (the Stripe item), if it did. */
  purchasedSince: isoDateTimeSchema.nullable(),
  endsAt: isoDateTimeSchema.nullable(),
  grant: z.object({ until: isoDateSchema.nullable() }).nullable(),
});
export type AdminAiAssistant = z.infer<typeof adminAiAssistantSchema>;

/** Give AI Assistant free (`until` null: no last day), or take the grant back (`grant: null`). */
export const updateAiAssistantRequestSchema = z.object({
  grant: z.object({ until: isoDateSchema.nullable() }).nullable(),
});
export type UpdateAiAssistantRequest = z.infer<typeof updateAiAssistantRequestSchema>;

/**
 * The review list in Control Center: one row for each question that somebody asked, newest first,
 * so Teamna sees what people ask and which answers do not work. Each opened chat writes an audit
 * row with the staff email.
 */
/** "2026-10-21T09:15:02.123456Z|<turn id>": a review page cursor. */
export const assistantReviewCursorSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z\|[0-9a-f-]{36}$/)
  .refine((value) => {
    const [at, id] = value.split("|");
    // A real instant (the date parts round-trip) and a real uuid.
    const date = new Date(`${at!.slice(0, 23)}Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 19) === at!.slice(0, 19) && uuidSchema.safeParse(id).success;
  }, "Not a review cursor.");

export const adminAssistantReviewQuerySchema = z.object({
  businessId: uuidSchema.optional(),
  outcome: assistantOutcomeSchema.optional(),
  /** `none`: not rated. */
  rating: z.enum(["up", "down", "none"]).optional(),
  topic: assistantTopicSchema.optional(),
  /** Only questions whose plan the person did not confirm, or that ended stale or failed. */
  problems: z.enum(["true", "false"]).optional(),
  /** Days back from now, 1 to 90. Default 14. */
  days: z.coerce.number().int().min(1).max(90).default(14),
  /** Words in the prompt. */
  q: z.string().trim().max(100).optional(),
  /**
   * The `nextBefore` of the page before (keyset paging): the time of its last prompt to the
   * microsecond and the prompt id, so prompts at the same instant are never skipped.
   */
  before: assistantReviewCursorSchema.optional(),
});
export type AdminAssistantReviewQuery = z.infer<typeof adminAssistantReviewQuerySchema>;

export const adminAssistantReviewRowSchema = z.object({
  /** The prompt turn. */
  turnId: uuidSchema,
  conversationId: uuidSchema,
  businessId: uuidSchema,
  businessName: z.string(),
  /** owner or admin, at the time of the list. */
  askerRole: z.enum(["owner", "admin", "other"]),
  /** A suggestion chat names its kind; a question chat is null. */
  cardKind: assistantCardKindSchema.nullable(),
  prompt: z.string(),
  inputMode: assistantInputModeSchema.nullable(),
  topic: assistantTopicSchema.nullable(),
  /** The answer to this prompt. Null while it runs, or when the answer row is missing. */
  outcome: assistantOutcomeSchema.nullable(),
  rating: assistantRatingSchema.nullable(),
  ratingReason: assistantRatingReasonSchema.nullable(),
  /** The tool names of the answer, in call order. */
  tools: z.array(z.string()),
  /** What no tool could answer (outcome `no_tool`): the model's short words. Optional for older API builds. */
  gap: z.string().nullable().optional(),
  /** A tool of the answer failed. */
  toolError: z.boolean(),
  /** The status of the plan of the answer, if it made one. */
  plan: z.enum(assistantActionStatuses).nullable(),
  costMicros: z.number().int(),
  createdAt: isoDateTimeSchema,
});
export type AdminAssistantReviewRow = z.infer<typeof adminAssistantReviewRowSchema>;

export const adminAssistantReviewSchema = z.object({
  rows: z.array(adminAssistantReviewRowSchema),
  /** Pass as `before` for the next page. Null on the last page. */
  nextBefore: assistantReviewCursorSchema.nullable(),
  /** Counts over the whole filter, not the page. */
  totals: z.object({
    questions: z.number().int(),
    businesses: z.number().int(),
    up: z.number().int(),
    down: z.number().int(),
    errors: z.number().int(),
    refused: z.number().int(),
    /** Answers with outcome `no_tool`. Optional for older API builds. */
    noTool: z.number().int().optional(),
    costMicros: z.number().int(),
    topics: z.array(z.object({ topic: assistantTopicSchema.nullable(), count: z.number().int() })),
    /**
     * The things that no tool answers, the most asked first (20 at most): the next tools to build.
     * Optional for older API builds.
     */
    gaps: z
      .array(z.object({ need: z.string(), kind: assistantGapKindSchema, count: z.number().int(), businesses: z.number().int(), lastAt: z.string() }))
      .optional(),
  }),
});
export type AdminAssistantReview = z.infer<typeof adminAssistantReviewSchema>;

/** One turn as Control Center reads it: what Desk shows, plus what the screen hides. */
export const adminAssistantTurnSchema = assistantTurnSchema.extend({
  topic: assistantTopicSchema.nullable(),
  ratingReason: assistantRatingReasonSchema.nullable(),
  ratingNote: z.string().nullable(),
  model: z.string().nullable(),
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
  costMicros: z.number().int(),
});
export type AdminAssistantTurn = z.infer<typeof adminAssistantTurnSchema>;

export const adminAssistantConversationSchema = z.object({
  id: uuidSchema,
  businessId: uuidSchema,
  businessName: z.string(),
  title: z.string(),
  status: z.enum(["open", "snoozed", "done", "closed"]),
  cardKind: assistantCardKindSchema.nullable(),
  /** The person who asked: name and role at the business. */
  asker: z.object({ name: z.string().nullable(), role: z.enum(["owner", "admin", "other"]) }),
  turns: z.array(adminAssistantTurnSchema),
  actions: z.array(
    assistantActionSchema.extend({
      input: z.record(z.string(), z.unknown()),
      title: z.string(),
      decidedAt: isoDateTimeSchema.nullable(),
    }),
  ),
  createdAt: isoDateTimeSchema,
});
export type AdminAssistantConversation = z.infer<typeof adminAssistantConversationSchema>;
